/**
 * Centralized Authentication Service
 * 
 * Handles token acquisition with retry logic to ensure valid tokens are returned.
 * This is critical for new user signups where the Post-User-Registration Action
 * may not have completed before the app tries to make API calls.
 * 
 * Problem it solves:
 * 1. User signs up → Auth0 creates user
 * 2. Post-User-Registration Action assigns user to org (async)
 * 3. Initial token may not have org context yet
 * 4. API calls fail with 403
 * 5. This service retries with fresh tokens until success or max retries
 */

const MAX_AUTH_RETRIES = 3;
const RETRY_DELAY_MS = 2000;

/**
 * Get access token with retry logic for new user registrations.
 * 
 * On first call, uses cached token. If API call fails with 403,
 * caller should use getAccessTokenWithRetry to force a fresh token.
 * 
 * @param {Function} getAccessTokenSilently - Auth0's getAccessTokenSilently function
 * @param {Object} options - Options object
 * @param {boolean} options.forceRefresh - Force a fresh token (bypass cache)
 * @returns {Promise<string>} Access token
 */
export const getAccessToken = async (getAccessTokenSilently, options = {}) => {
  const { forceRefresh = false } = options;
  
  try {
    const token = await getAccessTokenSilently({
      cacheMode: forceRefresh ? 'off' : 'on'
    });
    return token;
  } catch (error) {
    console.error('Failed to get access token:', error);
    throw error;
  }
};

/**
 * Make an authenticated API call with automatic retry on auth failures.
 * 
 * This handles the race condition where:
 * - User just signed up
 * - Post-User-Registration Action hasn't completed yet
 * - First API call fails with 403
 * - We retry with a fresh token
 * 
 * @param {Function} getAccessTokenSilently - Auth0's getAccessTokenSilently function
 * @param {Function} apiCall - Function that takes a token and returns a Promise
 * @param {Object} options - Options object
 * @param {number} options.maxRetries - Maximum number of retries (default: 3)
 * @param {number} options.retryDelay - Delay between retries in ms (default: 2000)
 * @returns {Promise<{data: any, error: any}>} API response
 */
export const callWithAuthRetry = async (getAccessTokenSilently, apiCall, options = {}) => {
  const { 
    maxRetries = MAX_AUTH_RETRIES, 
    retryDelay = RETRY_DELAY_MS 
  } = options;
  
  let lastError = null;
  let attempt = 0;
  
  console.log(`🔐 callWithAuthRetry starting (maxRetries: ${maxRetries}, retryDelay: ${retryDelay}ms)`);
  
  while (attempt <= maxRetries) {
    try {
      // First attempt uses cached token, retries force fresh token
      const forceRefresh = attempt > 0;
      console.log(`🔐 Attempt ${attempt + 1}/${maxRetries + 1}: Getting token (forceRefresh: ${forceRefresh})`);
      const token = await getAccessToken(getAccessTokenSilently, { forceRefresh });
      
      // Make the API call
      console.log(`🔐 Attempt ${attempt + 1}/${maxRetries + 1}: Making API call...`);
      const result = await apiCall(token);
      console.log(`🔐 Attempt ${attempt + 1}/${maxRetries + 1}: API call result:`, { 
        hasData: !!result.data, 
        hasError: !!result.error,
        errorStatus: result.error?.status,
        errorMessage: result.error?.message?.substring(0, 100)
      });
      
      // Check if the result indicates an auth error (401/403)
      if (result.error) {
        const errorStatus = result.error.status;
        const errorMessage = result.error.message || String(result.error);
        
        // Check status code first (most reliable), then fall back to message matching
        const isAuthError = errorStatus === 401 || 
                           errorStatus === 403 ||
                           errorMessage.includes('401') ||
                           errorMessage.includes('403') || 
                           errorMessage.includes('Forbidden') ||
                           errorMessage.includes('Unauthorized') ||
                           errorMessage.includes('organization') ||
                           errorMessage.includes('not authorized') ||
                           errorMessage.includes('Authentication');
        
        console.log(`🔐 Attempt ${attempt + 1}/${maxRetries + 1}: isAuthError=${isAuthError}, errorStatus=${errorStatus}, shouldRetry=${isAuthError && attempt < maxRetries}`);
        
        if (isAuthError && attempt < maxRetries) {
          console.log(`🔄 Auth retry attempt ${attempt + 1}/${maxRetries} - got ${errorStatus || 'auth error'}, waiting ${retryDelay}ms then fetching fresh token...`);
          lastError = result.error;
          attempt++;
          await sleep(retryDelay);
          continue;
        }
      }
      
      // Success or non-auth error
      console.log(`🔐 Attempt ${attempt + 1}/${maxRetries + 1}: Returning result (success or non-retryable error)`);
      return result;
      
    } catch (error) {
      console.error(`🔐 API call EXCEPTION (attempt ${attempt + 1}):`, error);
      lastError = error;
      
      // Check if it's an auth error
      const errorStatus = error.response?.status || error.status;
      const isAuthError = errorStatus === 401 ||
                         errorStatus === 403 || 
                         error.message?.includes('401') ||
                         error.message?.includes('403') ||
                         error.message?.includes('Forbidden') ||
                         error.message?.includes('Unauthorized');
      
      console.log(`🔐 Exception: isAuthError=${isAuthError}, errorStatus=${errorStatus}, shouldRetry=${isAuthError && attempt < maxRetries}`);
      
      if (isAuthError && attempt < maxRetries) {
        console.log(`🔄 Auth retry attempt ${attempt + 1}/${maxRetries} - caught ${errorStatus || 'auth error'}, waiting ${retryDelay}ms then fetching fresh token...`);
        attempt++;
        await sleep(retryDelay);
        continue;
      }
      
      // Non-retryable error or max retries reached
      console.log(`🔐 Returning error after ${attempt + 1} attempts (exception path)`);
      return { data: null, error: { message: error.message, status: errorStatus } };
    }
  }
  
  // Max retries exceeded
  console.error(`❌ Max auth retries (${maxRetries}) exceeded`);
  return { 
    data: null, 
    error: lastError || { message: 'Authentication failed after multiple retries' } 
  };
};

/**
 * Sleep utility for retry delays
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Check if an error indicates an authentication/authorization issue
 * that might be resolved by getting a fresh token.
 * 
 * @param {any} error - Error object or message
 * @returns {boolean} True if error is likely auth-related
 */
export const isAuthError = (error) => {
  if (!error) return false;
  
  const message = error.message || error.toString();
  const status = error.response?.status || error.status;
  
  return status === 401 || 
         status === 403 || 
         message.includes('403') ||
         message.includes('401') ||
         message.includes('Forbidden') ||
         message.includes('Unauthorized') ||
         message.includes('organization') ||
         message.includes('not authorized');
};
